import React, { use, useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { useFormik } from "formik";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { formatPhoneNumbers } from '../_helpers/formatter';
import {
    Form,
    Button,
    Divider,
    Grid,
    Header,
    Checkbox,
    Label,
} from 'semantic-ui-react';

import { 
    alertService, 
    providerService,
    facilityService,
    userService
} from '../_services';

import { Countries, States, MaxOtherCords } from '../models/Constants';
import { Link } from 'react-router-dom';
import {verifyAddress} from '../_helpers/verify-address';

function AddEdit({ history, match }) {
    const { id } = match.params;
    const isAddMode = !id;
    const pathname = window.location.pathname;
    const [isLoading, setLoading] = useState(false);
    const [facility, setFacility] = useState({});
    const [clinicalSiteManagers, setClinicalSiteManagers] = useState([]);
    const [siteProviders, setSiteProviders] = useState([]);
    const [currPrimaryProvider, setPrimaryProvider] = useState({});
    const [primaryStudyCoordinator, setPrimaryStudyCoordinator] = useState(null);
    const [numStudyCoordinators, setNumStudyCoordinators] = useState(1);
    const [otherStudyCoordinator1, setOtherStudyCoordinator1] = useState({});
    const [otherStudyCoordinator2, setOtherStudyCoordinator2] = useState({});
    const [otherStudyCoordinator3, setOtherStudyCoordinator3] = useState({});
    const [otherStudyCoordinator4, setOtherStudyCoordinator4] = useState({});
    const [useNewSiteInvestigator, setUseNewSiteInvestigator] = useState(true);
    // View mode
    const [viewMode, setViewMode] = useState(false);

    // form validation rules
    const validationSchema = Yup.object().shape({
        site_info: Yup.object().shape({
            facility_name: Yup.string()
                .required('Name is required'),
            address: Yup.object().shape({
                street_address_1: Yup.string()
                    .required('Street Address 1 is required')
                    .max(100, "Street Address must be 100 characters or less"),
                street_address_2: Yup.string()
                    .max(100, "Street Address must be 100 characters or less"),
                city: Yup.string()
                    .required('City is required')
                    .max(50, "City must be 50 characters or less"),
                state: Yup.string()
                    .required('State is required'),
                postal_code: Yup.string()
                    .matches(/^\d{5}$/, 'Postal Code must be 5 digits')
                    .required('Postal Code is required'),
                country: Yup.string()
                    .required("Country is required"),
            }),
            primary_contact_id: Yup.string()
                .required('Field Rep name is required'),
            on_call_phone: Yup.string()
                .max(12, 'Phone number must be no more than 12 digits long')
                .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
            all_day_phone: Yup.string()
                .max(12, 'Phone number must be no more than 12 digits long')
                .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
            alt_recipient_first_name: Yup.string(),
            alt_recipient_last_name: Yup.string(),
            alt_recipient_phone: Yup.string()
                        .max(12, 'Phone number must be no more than 12 digits long')
                        .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
            alt_recipient_email: Yup.string(),
            pas: Yup.boolean(),
        }),
        primary_provider: Yup.object().shape({
            first_name: Yup.string(),
            last_name: Yup.string().when('first_name', {
                is: (val) => !!val,
                then: (schema) => schema.required('Last name is required'),
                otherwise: (schema) => schema.notRequired(),
            }),
            external_user_id: Yup.string().when('first_name', {
                is: (val) => !!val,
                then: (schema) => schema.required('NPI is required')
                                        .min(10, 'NPI must be 10 digits.')
                                        .max(10, 'NPI must be 10 digits.'),
                otherwise: (schema) => schema.notRequired()
            }),
            email: Yup.string().when('first_name', {
                is: (val) => !!val,
                then: (schema) => schema.required('Email is required')
                                        .matches(/^(?=.*@)(?=.*\.).*$/, 'email must be valid'),
                otherwise: (schema) => schema.notRequired()
            }),
            phone_number: Yup.string().when('first_name', {
                is: (val) => !!val,
                then: (schema) => schema.notRequired('Phone number is required')
                                        .max(12, 'Phone number must be no more than 12 digits long')
                                        .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
                otherwise: (schema) => schema.notRequired(),
            }),
            id: Yup.number()
        }, [['first_name'], ['last_name']]),
        primary_facility_coordinator: Yup.object().shape({
            first_name: Yup.string(),
            last_name: Yup.string(),
            email: Yup.string().when('first_name', {
                is: (val) => !!val,
                then: (schema) => schema.required('Email is required')
                                        .matches(/^(?=.*@)(?=.*\.).*$/, 'email must be valid'),
                otherwise: (schema) => schema.notRequired()
            }),
            phone: Yup.string().when('first_name', {
                is: (val) => !!val,
                then: (schema) => schema.notRequired('Phone number is required')
                                        .max(12, 'Phone number must be no more than 12 digits long')
                                        .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        other_study_coordinator_1: Yup.object().shape({
            first_name: Yup.string(),
            last_name: Yup.string(),
            email: Yup.string(),
            phone_number: Yup.string()
                .max(12, 'Phone number must be no more than 12 digits long')
                .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
        }),
        other_study_coordinator_2: Yup.object().shape({
            first_name: Yup.string(),
            last_name: Yup.string(),
            email: Yup.string(),
            phone_number: Yup.string()
                .max(12, 'Phone number must be no more than 12 digits long')
                .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
        }),
        other_study_coordinator_3: Yup.object().shape({
            first_name: Yup.string(),
            last_name: Yup.string(),
            email: Yup.string(),
            phone_number: Yup.string()
                .max(12, 'Phone number must be no more than 12 digits long')
                .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
        }),
        other_study_coordinator_4: Yup.object().shape({
            first_name: Yup.string(),
            last_name: Yup.string(),
            email: Yup.string(),
            phone_number: Yup.string()
                .max(12, 'Phone number must be no more than 12 digits long')
                .matches(/^(1-)?\d{3}-\d{3}-\d{4}$/, 'Phone number must be in the format xxx-xxx-xxxx'),
        })
    });

    const formik = useFormik({
        initialValues: {
            site_info: {
                facility_name: "",
                address: {
                    street_address_1: "",
                    street_address_2: "",
                    city: "",
                    state: "",
                    postal_code: "",
                    country: ""
                },
                primary_contact_id: "",
                on_call_phone: "",
                all_day_phone: "",
                alt_recipient_first_name: "",
                alt_recipient_last_name: "",
                alt_recipient_phone: "",
                alt_recipient_email: "",
                pas: false,
            },
            primary_provider: {
                id:"",
                first_name: "",
                last_name: "",
                email: "",
                phone_number: "",
                external_user_id: "",
                facility_id: "",
                facilities_to_assign:[],
                facilities_to_unassign:[],
                facilities:[],
                role_name: "OUTPATIENT_PROVIDER",
                is_primary: false
            },
            primary_facility_coordinator: {
                first_name: "",
                last_name: "",
                external_user_id: "",
                email: "",
                phone_number: "",
                facility_id: "",
                facilities_to_assign:[],
                facilities_to_unassign:[],
                facilities:[],
                role_name: "FACILITY_ADMIN",
                is_primary: true
            },
            other_study_coordinator_1: {
                first_name: "",
                last_name: "",
                external_user_id: "",
                email: "",
                phone_number: "",
                facility_id: "",
                facilities_to_assign:[],
                facilities_to_unassign:[],
                facilities:[],
                role_name: "FACILITY_ADMIN",
                is_primary: false
            },
            other_study_coordinator_2: {
                first_name: "",
                last_name: "",
                external_user_id: "",
                email: "",
                phone_number: "",
                facility_id: "",
                facilities_to_assign:[],
                facilities_to_unassign:[],
                facilities:[],
                role_name: "FACILITY_ADMIN",
                is_primary: false
            },
            other_study_coordinator_3: {
                first_name: "",
                last_name: "",
                external_user_id: "",
                email: "",
                phone_number: "",
                facility_id: "",
                facilities_to_assign:[],
                facilities_to_unassign:[],
                facilities:[],
                role_name: "FACILITY_ADMIN",
                is_primary: false
            },
            other_study_coordinator_4: {
                first_name: "",
                last_name: "",
                external_user_id: "",
                email: "",
                phone_number: "",
                facility_id: "",
                facilities_to_assign:[],
                facilities_to_unassign:[],
                facilities:[],
                role_name: "FACILITY_ADMIN",
                is_primary: false
            }
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (formData) => {
            onSubmit(formData);
        }
    });

    // functions to build form returned by useForm() hook
    const { setValue } = useForm({
        resolver: yupResolver(validationSchema)
    });

    /**
     * Insert Facility Info into Provider Dict
     * 
     * @param {Object} obj - json object 
     * @param {string} facilityId  
     * @param {boolean} pas
     * @param {Object} facilityDict 
     * @returns {void}
     */
    const insertFacilityInfo = (obj, facilityId, pas, facilityDict) => {
        obj.facility_id = facilityId;
        obj.pas = pas
        obj.facilities.push(facilityDict);
    }

    /**
    * @param {object} data
    * @returns {Promise<void>}
    */
    async function onSubmit(data) {
        var submissionData = JSON.parse(JSON.stringify(data));
        // Strip dashes from numbers
        //submissionData["site_info"]["pas"] = submissionData["pas"]
        //delete submissionData['pas']
        formatPhoneNumbers(submissionData, 'remove', [
            'site_info.all_day_phone',
            'primary_provider.phone_number',
            'primary_facility_coordinator.phone_number',
            'site_info.alt_recipient_phone',
            'other_study_coordinator_1.phone_number',
            'other_study_coordinator_2.phone_number',
            'other_study_coordinator_3.phone_number',
            'other_study_coordinator_4.phone_number',
        ]);
        let success = await verifyAddress(submissionData, 'site_info.address');
        if(!success){
            return formik.setSubmitting(false);
        }
        else{
            data['site_info']['address'] = submissionData['site_info']['address'];
        }
        

        return isAddMode
            ? await createFacilityAndProviders(submissionData)
            : await updateFacilityAndProviders(id, submissionData);
    }

    async function createFacilityAndProviders(data) {
        // Site on call phone comes from primary study coordinator
        data.site_info.on_call_phone = data.primary_facility_coordinator.phone_number;
        let facilityResponse;
        try{
            facilityResponse = await facilityService.create(data.site_info)
        }catch(error){
            alertService.error(error);
            formik.setSubmitting(false);
            return;
        }

        const facilityId = facilityResponse.facility_id.toString();
        
        // If the facilty created successfully we will try to create as much data as possible
        var providerResp, primaryCordResp, otherCord1Resp, otherCord2Resp, otherCord3Resp, otherCord4Resp;
        providerResp = primaryCordResp = otherCord1Resp = otherCord2Resp = otherCord3Resp = otherCord4Resp = true;
        data.primary_provider.facilities_to_assign.push({ "id": facilityId, "is_primary": false });
        if (!useNewSiteInvestigator) {
            const primaryProvID = data.primary_provider.id;
            delete data.primary_provider.id;
            delete data.primary_provider.facilities;
            delete data.primary_provider.role_name;
            delete data.primary_provider.is_primary;
            delete data.primary_provider.facility_id;
            data.primary_provider.pas = data.site_info.pas;
            providerResp = await providerService.update(primaryProvID, data.primary_provider).catch(() => {return false;});
        } else if (!isCoordinatorDataEmpty(data.primary_provider, true)){
            data.primary_provider.facility_id = facilityId;
            data.primary_provider.facilities.push({ "id": facilityId, "is_primary": false });
            data.primary_provider.pas = data.site_info.pas;
            delete data.primary_provider.id;
            providerResp = await providerService.create(data.primary_provider).catch(() => {return false;});
        }

        if(!isCoordinatorDataEmpty(data.primary_facility_coordinator)){
            insertFacilityInfo(data.primary_facility_coordinator, facilityId, data.site_info.pas, { "id": facilityId, "is_primary": true });
            primaryCordResp = await providerService.create(data.primary_facility_coordinator).catch(() => {return false;});
        }
        if(!isCoordinatorDataEmpty(data.other_study_coordinator_1)){
            insertFacilityInfo(data.other_study_coordinator_1, facilityId, data.site_info.pas, { "id": facilityId, "is_primary": false });
            otherCord1Resp = await providerService.create(data.other_study_coordinator_1).catch(() => {return false;});
        }
        if(!isCoordinatorDataEmpty(data.other_study_coordinator_2)){
            insertFacilityInfo(data.other_study_coordinator_2, facilityId, data.site_info.pas, { "id": facilityId, "is_primary": false });
            otherCord2Resp = await providerService.create(data.other_study_coordinator_2).catch(() => {return false;});
        }
        if(!isCoordinatorDataEmpty(data.other_study_coordinator_3)){
            insertFacilityInfo(data.other_study_coordinator_3, facilityId, data.site_info.pas, { "id": facilityId, "is_primary": false });
            otherCord3Resp = await providerService.create(data.other_study_coordinator_3).catch(() => {return false;});
        }
        if(!isCoordinatorDataEmpty(data.other_study_coordinator_4)){
            insertFacilityInfo(data.other_study_coordinator_4, facilityId, data.site_info.pas, { "id": facilityId, "is_primary": false });
            otherCord4Resp = await providerService.create(data.other_study_coordinator_4).catch(() => {return false;});
        }

        // If any of these got a bad response alert with error, else success
        if (!providerResp || !primaryCordResp || !otherCord1Resp || !otherCord2Resp || !otherCord3Resp || !otherCord4Resp){
            alertService.error('Facility create, but there was an issue adding a cordinator. Use update screen to fix.');
            history.push('/facilities');
        }else{
            alertService.success('Facility and providers added', { keepAfterRouteChange: true });
            history.push('/facilities');
        }
    }

    async function updateOtherCoordinators(data) {
        console.log(`Number of Existing Study Coordinators: ${numStudyCoordinators}`);
        var i = 0;
        
        for (i = 1; i < MaxOtherCords+1; i++){
            if (i < numStudyCoordinators || (i == numStudyCoordinators && primaryStudyCoordinator == null)){
                // If we have less than number of existing or primary_coord is null we update
                console.log(`Updating other_study_coordinator_${i}...`);
                var cord = data[`other_study_coordinator_${i}`];
                delete cord.role_name;
                delete cord.is_primary;
                delete cord.facility_id;
                delete cord.facilities;
                cord.pas = data.site_info.pas;
                var cord_id = 0;
                if (i == 1){
                    cord_id = otherStudyCoordinator1;
                }else if ( i == 2){
                    cord_id = otherStudyCoordinator2;
                }else if ( i == 3){
                    cord_id = otherStudyCoordinator3;
                }else{
                    cord_id = otherStudyCoordinator4;
                }
                providerService.update(cord_id, cord).catch((error));
            }else{
                // Otherwise we create the new coordinator if not empty
                var cord = data[`other_study_coordinator_${i}`];
                if(!isCoordinatorDataEmpty(cord)){
                    console.log(`Creating other_study_coordinator_${i}...`);
                    insertFacilityInfo(cord, id, data.site_info.pas, { "id": id, "is_primary": false });
                    providerService.create(cord).catch((error));
                }
            }
        }
    }

    function pushSuccess(){
        alertService.success('Facility and providers updated', { keepAfterRouteChange: true });
        history.push('/facilities');
    }

    async function updateFacilityAndProviders(id, data) {
        var facilityId = id.toString();
        // Site on call phone comes from primary study coordinator
        data.site_info.on_call_phone = data.primary_facility_coordinator.phone_number;
        try {
            await facilityService.update(facilityId, data.site_info);
        }catch (error){
            formik.setSubmitting(false);
            alertService.error(error);
            return;
        }         

        data.primary_facility_coordinator.facilities_to_assign.push({ "id": facilityId, "is_primary": false });
        data.primary_provider.pas = data.site_info.pas;
        
        var providerResp = true;
        
        let primary_provider_empty = isCoordinatorDataEmpty(data.primary_provider, true);
        const sameProvider = currPrimaryProvider == data.primary_provider.id;
        const primaryProviderId = data.primary_provider.id;
        delete data.primary_provider.id;
        // If not currently assigned provider 
        if(useNewSiteInvestigator && !primary_provider_empty){
            if(currPrimaryProvider != undefined){
                // unassign old provider
                // Unassign facility from old provider
                let currProv = siteProviders.find(function( prov ) {
                    return prov.key == currPrimaryProvider;
                })
                const unassign_prov_info = {
                    first_name: currProv.data?.first_name,
                    last_name: currProv.data?.last_name,
                    email: currProv.data?.email,
                    phone_number: currProv.data?.phone_number,
                    external_user_id: currProv.data?.npi,
                    facilities_to_assign: [],
                    facilities_to_unassign: [{"id": facilityId, "is_primary": false }],
                };
                let unassignResp = await providerService.update(currPrimaryProvider, unassign_prov_info).catch(() => {})
            }
            //create new provider
            data.primary_provider.facility_id = facilityId;
            data.primary_provider.facilities.push({ "id": facilityId, "is_primary": false });
            data.primary_provider.facilities_to_assign.push({ "id": facilityId, "is_primary": false });
            data.primary_provider.pas = data.site_info.pas;
            providerResp = await providerService.create(data.primary_provider).catch(() => {return false;});
            
        // Else we update the existing provider
        }else if (!primary_provider_empty){
            if(!sameProvider){
                // Push facility to assign to new provider
                data.primary_provider.facilities_to_assign.push({ "id": facilityId, "is_primary": false });

                // Unassign facility from old provider
                let currProv = siteProviders.find(function( prov ) {
                    return prov.key == currPrimaryProvider;
                })
                const unassign_prov_info = {
                    first_name: currProv.data?.first_name,
                    last_name: currProv.data?.last_name,
                    email: currProv.data?.email,
                    phone_number: currProv.data?.phone_number,
                    external_user_id: currProv.data?.npi,
                    facilities_to_assign: [],
                    facilities_to_unassign: [{"id": facilityId, "is_primary": false }],
                };
                let unassignResp = await providerService.update(currPrimaryProvider, unassign_prov_info).catch(() => {})
            }
            delete data.primary_provider.role_name;
            delete data.primary_provider.is_primary;
            delete data.primary_provider.facility_id;
            delete data.primary_provider.facilities;
            providerResp = await providerService.update(primaryProviderId, data.primary_provider).catch(() => {return false;});
        }

        if(!providerResp){
            alertService.error('Failed to save provider.');
            formik.setSubmitting(false);
            return;
        }
        
        if(primaryStudyCoordinator == null){
            if(!isCoordinatorDataEmpty(data.primary_facility_coordinator)){
                var creation_data = data.primary_facility_coordinator;
                creation_data.facility_id = facilityId;
                creation_data.facilities = [{"id": facilityId, "is_primary": true}];
                await providerService.create(creation_data)
            }
            updateOtherCoordinators(data).then((_) => pushSuccess());
        }else{
            delete data.primary_facility_coordinator.role_name;
            delete data.primary_facility_coordinator.is_primary;
            delete data.primary_facility_coordinator.facility_id;
            delete data.primary_facility_coordinator.facilities;
            data.primary_facility_coordinator.pas = data.site_info.pas
            await providerService.update(primaryStudyCoordinator, data.primary_facility_coordinator)
            .then((studyCoordinatorResponse) => updateOtherCoordinators(data))
            .then((_) => pushSuccess());
        }
    }

    const handleOptionChange = (option, field) => {
        formik.setFieldValue(field, option);
    }

    const handleProviderOptionChange = async (value, data, field) => {
        const primaryProvider = data.find(function( prov ) {
            return prov.value == value;
        });
        formatPhoneNumbers(primaryProvider, 'add', ['data.phone_number'])
        formik.setFieldValue(field, value);
        await formik.setValues({
            ...formik.values,
            primary_provider: {
                ...formik.values.primary_provider,
                id: primaryProvider.data.user_id,
                first_name: primaryProvider.data.first_name,
                last_name: primaryProvider.data.last_name,
                external_user_id: primaryProvider.data.npi,
                email: primaryProvider.data.email,
                phone: primaryProvider.data.phone_number,
            }
        });
    }

    const loadClinicalSiteManagers = () => {
        userService.getUserByRole("FIELD_REP")
        .then((_clinicalSiteManagers) => {
            if(_clinicalSiteManagers) {
                // Set Clinical Site Manager options
                const clinicalSiteManagerOptions = [];
                _clinicalSiteManagers.data[0].map(x => {
                    clinicalSiteManagerOptions.push(
                        {
                            key: x.user_id,
                            text: x.first_name + " " + x.last_name,
                            value: x.user_id
                        }
                    );
                });
                setClinicalSiteManagers(clinicalSiteManagerOptions);
            }
        })
        .catch((err) => {
            console.log("No field resps found!");
        });
    }

    const loadClinicalProviders = () => {
        userService.getUserByRole("OUTPATIENT_PROVIDER")
        .then((_siteProviders) => {
            if(_siteProviders) {
                // Set Clinical Providers options
                const siteProviderOptions = [];
                _siteProviders.data[0].map(x => {
                    siteProviderOptions.push(
                        {
                            key: x.user_id,
                            text: x.first_name + " " + x.last_name,
                            value: x.user_id,
                            data: x
                        }
                    );
                });
                setSiteProviders(siteProviderOptions);
            }
        })
        .catch((err) => {
            console.log("No primary providers found!");
        });
    }

    /**
     * Returns true/false if the object passed has expected first_name, last_name, email, phone_number and is not empty.
     * If isProvider==true then also checks for npi
     * @param {Object} coordinator 
     * @param {boolean} isProvider - Defaults to false, if true validates primary provider info.
     * @returns {boolean} 
     */
    const isCoordinatorDataEmpty = (coordinator, isProvider=false) => {
        let isDataEmpty = (coordinator.first_name === "") || (coordinator.last_name === "") ||
                        (coordinator.email === "") || (coordinator.phone_number === "");

        if (isProvider) return isDataEmpty || !coordinator.external_user_id;
        return isDataEmpty;
    }

    useEffect(() => {
        if (pathname.includes("view")) {
            setViewMode(true);
        } else {
            setViewMode(false);
        }
    }, [pathname]);

    // Loading wheel for edit/view
    useEffect(() => {
        if (window.onload || isAddMode) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [window]);

    useEffect(() => {
        // Load options for clinical site managers
        loadClinicalSiteManagers();
        loadClinicalProviders();

        if (isAddMode) {
            // Set USA as default country
            formik.setFieldValue("site_info.address.country", "USA");
        }
        else {
            // get facility and related providers by id
            facilityService.getSitesProvidersById(id)
            .then(async facilityResponse => {
                const facilityData = facilityResponse.data;
                formatPhoneNumbers(facilityData,'add', [
                    'site_info.alt_recipient_phone', 'site_info.all_day_phone', 'primary_provider.phone_number', 'site_info.on_call_phone'
                ]);
                // Set facility id
                setFacility(facilityData.site_info.id);
                delete facilityData.site_info.id;

                //Set Facility Data
                await formik.setValues({
                    ...formik.values, 
                    site_info: {
                        ...formik.values.site_info,
                        ...facilityData.site_info
                    },
                    primary_provider: {
                        ...formik.values.primary_provider,
                        id: facilityData.primary_provider?.id,
                        first_name: facilityData.primary_provider?.first_name,
                        last_name: facilityData.primary_provider?.last_name,
                        email: facilityData.primary_provider?.email,
                        phone_number: facilityData.primary_provider?.phone_number,
                        external_user_id: facilityData.primary_provider?.external_id
                    }
                });

                // Set clinical site manager data
                setPrimaryProvider(facilityData.primary_provider?.id);
                if(facilityData.primary_provider?.id != null){
                    setUseNewSiteInvestigator(false);
                }
                
                // Set study coordinator data
                if (facilityData.study_coordinator) {
                    setNumStudyCoordinators(facilityData.study_coordinator.length);
                    if (facilityData.study_coordinator.length > 0) {
                        let firstOtherCoordinatorSet = false;
                        let secondOtherCoordinatorSet = false;
                        let thirdOtherCoordinatorSet = false;
                        let fourthOtherCoordinatorSet = false;
                        for (let i = 0; i < facilityData.study_coordinator.length; i++) {
                            formatPhoneNumbers(facilityData.study_coordinator[i], 'add', ['phone_number']);
                            if (facilityData.study_coordinator[i].is_primary) {
                                // Set primary study coordinator data
                                await formik.setValues(prevValues => ({
                                    ...prevValues,
                                    primary_facility_coordinator: {
                                        ...prevValues.primary_facility_coordinator,
                                        first_name: facilityData.study_coordinator[i]?.first_name,
                                        last_name: facilityData.study_coordinator[i]?.last_name,
                                        email: facilityData.study_coordinator[i]?.email,
                                        phone_number: facilityData.study_coordinator[i]?.phone_number,
                                    }
                                }));
                                setPrimaryStudyCoordinator(facilityData.study_coordinator[i]?.id);
                            }else if (!firstOtherCoordinatorSet) {
                                // Set other study coordinator 1 data
                                await formik.setValues(prevValues => ({
                                    ...prevValues,
                                    other_study_coordinator_1: {
                                        ...prevValues.other_study_coordinator_1,
                                        first_name: facilityData.study_coordinator[i]?.first_name,
                                        last_name: facilityData.study_coordinator[i]?.last_name,
                                        email: facilityData.study_coordinator[i]?.email,
                                        phone_number: facilityData.study_coordinator[i]?.phone_number,
                                    }
                                }));
                                setOtherStudyCoordinator1(facilityData.study_coordinator[i]?.id);
                                firstOtherCoordinatorSet = true;
                            }
                            else if (!secondOtherCoordinatorSet) {
                                // Set other study coordinator 2 data
                                await formik.setValues(prevValues => ({
                                    ...prevValues,
                                    other_study_coordinator_2: {
                                        ...prevValues.other_study_coordinator_2,
                                        first_name: facilityData.study_coordinator[i]?.first_name,
                                        last_name: facilityData.study_coordinator[i]?.last_name,
                                        email: facilityData.study_coordinator[i]?.email,
                                        phone_number: facilityData.study_coordinator[i]?.phone_number,
                                    }
                                }));
                                setOtherStudyCoordinator2(facilityData.study_coordinator[i]?.id);
                                secondOtherCoordinatorSet = true;
                            }
                            else if (!thirdOtherCoordinatorSet) {
                                // Set other study coordinator 3 data
                                await formik.setValues(prevValues => ({
                                    ...prevValues,
                                    other_study_coordinator_3: {
                                        ...prevValues.other_study_coordinator_3,
                                        first_name: facilityData.study_coordinator[i]?.first_name,
                                        last_name: facilityData.study_coordinator[i]?.last_name,
                                        email: facilityData.study_coordinator[i]?.email,
                                        phone_number: facilityData.study_coordinator[i]?.phone_number,
                                    }
                                }));
                                setOtherStudyCoordinator3(facilityData.study_coordinator[i]?.id);
                                thirdOtherCoordinatorSet = true;
                            }
                            else if (!fourthOtherCoordinatorSet) { 
                                // Set other study coordinator 4 data
                                await formik.setValues(prevValues => ({
                                    ...prevValues,
                                    other_study_coordinator_4: {
                                        ...prevValues.other_study_coordinator_4,
                                        first_name: facilityData.study_coordinator[i]?.first_name,
                                        last_name: facilityData.study_coordinator[i]?.last_name,
                                        email: facilityData.study_coordinator[i]?.email,
                                        phone_number: facilityData.study_coordinator[i]?.phone_number,
                                    }
                                }));
                                setOtherStudyCoordinator4(facilityData.study_coordinator[i]?.id);
                                fourthOtherCoordinatorSet = true;
                            }
                        }
                    }
                }
                setLoading(false);
            })
            .catch(err => {
                alertService.error(err);
                formik.setSubmitting(false);
            })
        }
    }, []);


    return (
        <Form className="pl-5" loading={isLoading} onSubmit={formik.handleSubmit}>
            <div className="mt-4">
                <Header as='h3' dividing>Facility Details</Header>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Facility Name"
                        placeholder="Facility Name"
                        name="site_info.facility_name"
                        width={4}
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.facility_name}
                        value={formik.values.site_info?.facility_name}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="24-Hour Number"
                        placeholder="xxx-xxx-xxxx"
                        name="site_info.all_day_phone"
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.all_day_phone}
                        value={formik.values.site_info?.all_day_phone}
                        width={2}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Shipping Address Line 1"
                        placeholder="Shipping Address Line 1"
                        name="site_info.address.street_address_1"
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.address?.street_address_1}
                        value={formik.values.site_info?.address?.street_address_1}
                        width={4}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="City"
                        placeholder="City"
                        name="site_info.address.city"
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.address?.city}
                        value={formik.values.site_info?.address?.city}
                        width={2}
                    />
                    <Form.Select
                        fluid
                        readOnly={viewMode}
                        disabled={viewMode}
                        label="State"
                        placeholder="State"
                        name="site_info.address.state"
                        onChange={(event, data) => handleOptionChange(data.value, data.name)}
                        error={formik.errors.site_info?.address?.state}
                        value={formik.values.site_info?.address?.state}
                        width={2}
                        options={States}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Shipping Address Line 2"
                        placeholder="(Apt, Bldg, Unit)"
                        name="site_info.address.street_address_2"
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.address?.street_address_2}
                        value={formik.values.site_info?.address?.street_address_2}
                        width={4}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Zip Code"
                        placeholder="Zip Code"
                        name="site_info.address.postal_code"
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.address?.postal_code}
                        value={formik.values.site_info?.address?.postal_code}
                        width={2}
                    />
                    <Form.Select
                        fluid
                        readOnly={viewMode}
                        disabled={viewMode}
                        label="Country"
                        placeholder="Country"
                        name="site_info.address.country"
                        onChange={(event, data) => handleOptionChange(data.value, data.name)}
                        error={formik.errors.site_info?.address?.country}
                        value={formik.values.site_info?.address?.country}
                        width={2}
                        options={Countries}
                    />
                </Form.Group>
                <Form.Checkbox
                    type="checkbox"
                    label="Part of a Post Approval Study?"
                    name="site_info.pas"
                    checked={formik.values.site_info?.pas}
                    onChange={() => {
                        formik.setFieldValue('site_info.pas',!formik.values.site_info.pas);
                    }}
                />
            </div>
            <div className="pt-5">
            <Header as='h3' dividing>Field Rep</Header>
            <Form.Group>
                <Form.Select
                        fluid
                        readOnly={viewMode}
                        disabled={viewMode}
                        label="Field Rep Name"
                        placeholder="Name"
                        name="site_info.primary_contact_id"
                        onChange={(event, data) => handleOptionChange(data.value, data.name)}
                        error={formik.errors.site_info?.primary_contact_id}
                        value={formik.values.site_info?.primary_contact_id}
                        width={4}
                        options={clinicalSiteManagers ? clinicalSiteManagers : []}
                    />
                </Form.Group>
            </div>
            <div className="pt-5">
                <Header as='h3' dividing>Primary Provider</Header>
                <Form.Select
                        fluid
                        readOnly={viewMode}
                        disabled={useNewSiteInvestigator}
                        label="Primary Provider Name"
                        placeholder="Primary Provider Name"
                        name={"primary_provider.id"}
                        onChange={(event, data) => handleProviderOptionChange(data.value, data.options, data.name)}
                        error={formik.errors.primary_provider?.id}
                        value={formik.values.primary_provider?.id}
                        width={4}
                        options={siteProviders ? siteProviders : []}
                    />
                    <Form.Field>
                        <Checkbox toggle
                        disabled={viewMode}
                        label='Use Exisiting Provider'
                        onChange = { () => setUseNewSiteInvestigator(!useNewSiteInvestigator)}
                        checked={!useNewSiteInvestigator}
                        // onClick = { () => handleUseExistingProvider(formik.values.primary_provider)}
                        />
                    </Form.Field>

            </div>
            <div className="pt-5">
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Provider First Name"
                        placeholder="First Name"
                        name="primary_provider.first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_provider?.first_name}
                        value={formik.values.primary_provider?.first_name}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Provider Last Name"
                        placeholder="Last Name"
                        name="primary_provider.last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_provider?.last_name}
                        value={formik.values.primary_provider?.last_name}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="NPI"
                        placeholder="NPI (10 digits)"
                        name="primary_provider.external_user_id"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_provider?.external_user_id}
                        value={formik.values.primary_provider?.external_user_id}
                        width={2}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Provider Email"
                        placeholder="Email"
                        name="primary_provider.email"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_provider?.email}
                        value={formik.values.primary_provider?.email}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Provider Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="primary_provider.phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_provider?.phone_number}
                        value={formik.values.primary_provider?.phone_number}
                        width={3}
                    />
                </Form.Group>
            </div>
            <div className="pt-5">
                <Header as='h3' dividing>Primary Facility Coordinator</Header>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Facility Coordinator First Name"
                        placeholder="First Name"
                        name="primary_facility_coordinator.first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_facility_coordinator?.first_name}
                        value={formik.values.primary_facility_coordinator?.first_name}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Facility Coordinator Last Name"
                        placeholder="Last Name"
                        name="primary_facility_coordinator.last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_facility_coordinator?.last_name}
                        value={formik.values.primary_facility_coordinator?.last_name}
                        width={3}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Facility Coordinator Email"
                        placeholder="Email"
                        name="primary_facility_coordinator.email"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_facility_coordinator?.email}
                        value={formik.values.primary_facility_coordinator?.email}
                        width={3}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Primary Facility Coordinator Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="primary_facility_coordinator.phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.primary_facility_coordinator?.phone_number}
                        value={formik.values.primary_facility_coordinator?.phone_number}
                        width={3}
                    />
                </Form.Group>
                <Header as='h3' dividing>Other Facility Coordinators</Header>
                <Grid columns={2} divided>
                <Grid.Row >
                <Grid.Column class="d-flex justify-content-between">
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator First Name"
                        placeholder="First Name"
                        name="other_study_coordinator_1.first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_1?.first_name}
                        value={formik.values.other_study_coordinator_1?.first_name}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Last Name"
                        placeholder="Last Name"
                        name="other_study_coordinator_1.last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_1?.last_name}
                        value={formik.values.other_study_coordinator_1?.last_name}
                        width={6}
                    />
                </Form.Group>
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Email"
                        placeholder="Email"
                        name="other_study_coordinator_1.email"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_1?.email}
                        value={formik.values.other_study_coordinator_1?.email}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="other_study_coordinator_1.phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_1?.phone_number}
                        value={formik.values.other_study_coordinator_1?.phone_number}
                        width={6}
                    />
                </Form.Group>
                </Grid.Column>
                <Grid.Column>
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator First Name"
                        placeholder="First Name"
                        name="other_study_coordinator_2.first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_2?.first_name}
                        value={formik.values.other_study_coordinator_2?.first_name}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Last Name"
                        placeholder="Last Name"
                        name="other_study_coordinator_2.last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_2?.last_name}
                        value={formik.values.other_study_coordinator_2?.last_name}
                        width={6}
                    />
                </Form.Group>
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Email"
                        placeholder="Email"
                        name="other_study_coordinator_2.email"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_2?.email}
                        value={formik.values.other_study_coordinator_2?.email}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="other_study_coordinator_2.phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_2?.phone_number}
                        value={formik.values.other_study_coordinator_2?.phone_number}
                        width={6}
                    />
                </Form.Group>
                </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                <Grid.Column>
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator First Name"
                        placeholder="First Name"
                        name="other_study_coordinator_3.first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_3?.first_name}
                        value={formik.values.other_study_coordinator_3?.first_name}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Last Name"
                        placeholder="Last Name"
                        name="other_study_coordinator_3.last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_3?.last_name}
                        value={formik.values.other_study_coordinator_3?.last_name}
                        width={6}
                    />
                </Form.Group>
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Email"
                        placeholder="Email"
                        name="other_study_coordinator_3.email"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_3?.email}
                        value={formik.values.other_study_coordinator_3?.email}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="other_study_coordinator_3.phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_3?.phone_number}
                        value={formik.values.other_study_coordinator_3?.phone_number}
                        width={6}
                    />
                </Form.Group>
                </Grid.Column>
                <Grid.Column >
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator First Name"
                        placeholder="First Name"
                        name="other_study_coordinator_4.first_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_4?.first_name}
                        value={formik.values.other_study_coordinator_4?.first_name}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Last Name"
                        placeholder="Last Name"
                        name="other_study_coordinator_4.last_name"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_4?.last_name}
                        value={formik.values.other_study_coordinator_4?.last_name}
                        width={6}
                    />
                </Form.Group>
                <Form.Group style={{justifyContent:'space-around'}}>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Email"
                        placeholder="Email"
                        name="other_study_coordinator_4.email"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_4?.email}
                        value={formik.values.other_study_coordinator_4?.email}
                        width={6}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Other Facility Coordinator Phone"
                        placeholder="xxx-xxx-xxxx"
                        name="other_study_coordinator_4.phone_number"
                        onChange={formik.handleChange}
                        error={formik.errors.other_study_coordinator_4?.phone_number}
                        value={formik.values.other_study_coordinator_4?.phone_number}
                        width={6}
                    />
                </Form.Group>
                </Grid.Column>
                </Grid.Row>
                </Grid>
            </div>
            <div className="pt-5">
                <Header as='h3' dividing>PDR Alternate Recipient</Header>
                <Form.Group>
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Alternate Recipient First Name"
                        placeholder="First Name"
                        name='site_info.alt_recipient_first_name'
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.alt_recipient_first_name}
                        value={formik.values.site_info?.alt_recipient_first_name}
                        width={4}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Alternate Recipient Last Name"
                        placeholder="Last Name"
                        name='site_info.alt_recipient_last_name'
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.alt_recipient_last_name}
                        value={formik.values.site_info?.alt_recipient_last_name}
                        width={4}
                    />
                    <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Alternate Recipient Phone Number"
                        placeholder="xxx-xxx-xxxx"
                        name='site_info.alt_recipient_phone'
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.alt_recipient_phone}
                        value={formik.values.site_info?.alt_recipient_phone}
                        width={4}
                    />
                     <Form.Input
                        fluid
                        readOnly={viewMode}
                        label="Alternate Recipient Email"
                        placeholder="Email"
                        name='site_info.alt_recipient_email'
                        onChange={formik.handleChange}
                        error={formik.errors.site_info?.alt_recipient_email}
                        value={formik.values.site_info?.alt_recipient_email}
                        width={4}
                    />
                </Form.Group>
            </div>
            <div className="pt-5 mt-5 pl-2">
                <Form.Group>
                    <span>
                        <>{!formik.isValid?<Label color='red' content={`There are Invalid Fields - Please Update ${JSON.stringify(formik.errors)}`}/>:<></>}</>
                    </span>
                </Form.Group>
                <Form.Group>
                    <div className="pr-3">
                        <Button 
                            type="submit"
                            primary
                            onSubmit={formik.handleSubmit}
                            disabled={formik.isSubmitting || viewMode || !formik.isValid}  
                        >
                            {formik.isSubmitting && <span className="spinner-border spinner-border-sm mr-1"></span>}
                            Submit
                        </Button>
                    </div>
                    <Button secondary as={ Link } name='facilities' to='/facilities'>Cancel</Button>
                </Form.Group>
            </div>
        </Form>
    );
}

export { AddEdit };